/**
 * Preview empty state for a project whose sandbox is gone (`sandboxStatus = 'lost'`).
 *
 * The provider reported the sandbox missing and no durable copy exists, so the only
 * way forward is a fresh build from the plan. The single CTA calls the same start
 * action as the normal Start button; nothing starts a lost project automatically.
 *
 * @module
 */

import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'

export interface SandboxLostStateProps {
  /** The workspace's start action (same one the Start / Wake buttons call). */
  onStart: () => void
  /** Last start failure to show above the copy, if any. */
  error?: string | null
}

export function SandboxLostState({ onStart, error }: SandboxLostStateProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  return (
    <div
      className={cm.cn(
        cm.stack(4),
        cm.flex({ direction: 'col', align: 'center', justify: 'center' }),
      )}
      data-mol-id="workspace-sandbox-lost"
    >
      {error && (
        <p className={cm.cn(cm.textSize('sm'), cm.textError)} role="alert">
          {error}
        </p>
      )}
      <p
        className={cm.cn(cm.textSize('base'), cm.fontWeight('medium'))}
        style={{ maxWidth: 420, textAlign: 'center' }}
      >
        {t('sandbox.lost.message', undefined, {
          defaultValue:
            'This project’s sandbox is gone. Start the project again to build a fresh copy.',
        })}
      </p>
      {/* Primary action: design-system success button (same colour as the Start / Wake buttons), compact touch floor. */}
      <button
        type="button"
        data-mol-id="workspace-start-fresh"
        onClick={onStart}
        className={cm.cn(cm.button({ color: 'success', size: 'lg' }), cm.touchTargetCompact)}
      >
        {t('sandbox.lost.start', undefined, { defaultValue: 'Start fresh copy' })}
      </button>
    </div>
  )
}
